/**
 * 控制台与管理后台共用的文案：通用词、日期与状态、后端错误码。
 *
 * 背景：后端错误统一返回 {error: {code, message}}，message 是英文的开发者说明；用户看到的提示要按 code 映射成
 *   当前语言（PRD 7 章 packages/i18n）。
 * 设计意图：errors 按后端 APIError 的 code 逐个列出；没列到的 code 显示通用失败提示加 code，方便用户报给客服。
 * 关键约束：新增后端错误码时同步加在这里（rg 'APIError\(' src/asterove 可列全）；zh 为繁體中文（zh-TW）。
 */
import { translate, type Entry, type Locale } from './core.tsx'

export const C = {
  appName: { ko: 'Asterove', zh: 'Asterove', en: 'Asterove' },
  loading: { ko: '불러오는 중이에요', zh: '載入中', en: 'Loading' },
  save: { ko: '저장', zh: '儲存', en: 'Save' },
  saved: { ko: '저장했어요', zh: '已儲存', en: 'Saved' },
  cancel: { ko: '취소', zh: '取消', en: 'Cancel' },
  confirm: { ko: '확인', zh: '確認', en: 'Confirm' },
  close: { ko: '닫기', zh: '關閉', en: 'Close' },
  edit: { ko: '수정', zh: '編輯', en: 'Edit' },
  create: { ko: '만들기', zh: '新增', en: 'Create' },
  delete: { ko: '삭제', zh: '刪除', en: 'Delete' },
  back: { ko: '돌아가기', zh: '返回', en: 'Back' },
  next: { ko: '다음', zh: '下一步', en: 'Next' },
  search: { ko: '검색', zh: '搜尋', en: 'Search' },
  refresh: { ko: '새로고침', zh: '重新整理', en: 'Refresh' },
  copy: { ko: '복사', zh: '複製', en: 'Copy' },
  copied: { ko: '복사했어요', zh: '已複製', en: 'Copied' },
  copyFailed: { ko: '복사하지 못했어요. 직접 선택해서 복사해 주세요', zh: '複製失敗，請手動選取複製', en: 'Copy failed. Select and copy it manually' },
  loadMore: { ko: '더 보기', zh: '載入更多', en: 'Load more' },
  noData: { ko: '아직 데이터가 없어요', zh: '暫無資料', en: 'No data yet' },
  retry: { ko: '다시 시도', zh: '重試', en: 'Retry' },
  offlineTitle: { ko: '지금 서버에 연결할 수 없어요', zh: '暫時無法連線至伺服器', en: "Can't reach the server right now" },
  offlineHint: {
    ko: '로그인은 그대로 유지돼요. 연결되면 보던 화면으로 자동으로 돌아가요',
    zh: '登入狀態仍保留，恢復連線後會自動回到剛才的頁面',
    en: "You're still signed in. This page comes back on its own once the connection returns",
  },
  menu: { ko: '메뉴', zh: '選單', en: 'Menu' },
  logout: { ko: '로그아웃', zh: '登出', en: 'Log out' },
  language: { ko: '언어', zh: '介面語言', en: 'Language' },
  yes: { ko: '예', zh: '是', en: 'Yes' },
  no: { ko: '아니요', zh: '否', en: 'No' },
  on: { ko: '켜짐', zh: '開啟', en: 'On' },
  off: { ko: '꺼짐', zh: '關閉', en: 'Off' },
  none: { ko: '없음', zh: '無', en: 'None' },
  unlimited: { ko: '제한 없음', zh: '不限', en: 'Unlimited' },
  never: { ko: '없음', zh: '從未', en: 'Never' },
  all: { ko: '전체', zh: '全部', en: 'All' },
  optional: { ko: '선택', zh: '選填', en: 'Optional' },
  email: { ko: '이메일', zh: '電子郵件', en: 'Email' },
  password: { ko: '비밀번호', zh: '密碼', en: 'Password' },
  name: { ko: '이름', zh: '名稱', en: 'Name' },
  status: { ko: '상태', zh: '狀態', en: 'Status' },
  role: { ko: '역할', zh: '角色', en: 'Role' },
  createdAt: { ko: '만든 날짜', zh: '建立時間', en: 'Created' },
  time: { ko: '시간', zh: '時間', en: 'Time' },
  actions: { ko: '작업', zh: '操作', en: 'Actions' },
  reason: { ko: '사유', zh: '原因', en: 'Reason' },
  reasonHint: { ko: '감사 기록에 남아요', zh: '會寫入稽核記錄', en: 'Saved to the audit log' },
  petals: { ko: '꽃잎', zh: '花瓣', en: 'Petals' },
  petalsUnit: { ko: '{n} 꽃잎', zh: '{n} 花瓣', en: '{n} petals' },
  model: { ko: '모델', zh: '模型', en: 'Model' },
  requests: { ko: '요청 수', zh: '請求數', en: 'Requests' },
  failed: { ko: '실패', zh: '失敗', en: 'Failed' },
  inputTokens: { ko: '입력 토큰', zh: '輸入 Token', en: 'Input tokens' },
  cachedTokens: { ko: '캐시 입력', zh: '快取輸入', en: 'Cached input' },
  outputTokens: { ko: '출력 토큰', zh: '輸出 Token', en: 'Output tokens' },
  images: { ko: '이미지', zh: '圖片', en: 'Images' },
  cost: { ko: '비용', zh: '費用', en: 'Cost' },
  totpCode: { ko: '인증 앱 코드', zh: '驗證器動態碼', en: 'Authenticator code' },
  totpCodeHint: { ko: '인증 앱에 보이는 6자리 숫자', zh: '驗證器 App 顯示的 6 位數字', en: 'The 6-digit code in your authenticator app' },
  sessionExpired: { ko: '로그인이 만료됐어요. 다시 로그인해 주세요', zh: '登入已過期，請重新登入', en: 'Your session expired. Please log in again' },
  range7: { ko: '최근 7일', zh: '最近 7 天', en: 'Last 7 days' },
  range30: { ko: '최근 30일', zh: '最近 30 天', en: 'Last 30 days' },
  range90: { ko: '최근 90일', zh: '最近 90 天', en: 'Last 90 days' },
  orderStatus: {
    created: { ko: '결제 대기', zh: '待付款', en: 'Awaiting payment' },
    pending: { ko: '결제 확인 중', zh: '付款確認中', en: 'Confirming' },
    paid: { ko: '충전 처리 중', zh: '入帳中', en: 'Crediting' },
    fulfilled: { ko: '충전 완료', zh: '已入帳', en: 'Credited' },
    refunded: { ko: '환불됨', zh: '已退款', en: 'Refunded' },
    revoked: { ko: '회수됨', zh: '已追回', en: 'Revoked' },
    closed: { ko: '닫힘', zh: '已關閉', en: 'Closed' },
  },
  ledgerKind: {
    credit: { ko: '충전', zh: '儲值入帳', en: 'Top-up' },
    usage: { ko: '사용', zh: '呼叫消費', en: 'Usage' },
    bonus_grant: { ko: '보너스 지급', zh: '贈送', en: 'Bonus' },
    adjustment: { ko: '수동 조정', zh: '人工調整', en: 'Adjustment' },
    refund_clawback: { ko: '환불 회수', zh: '退款扣回', en: 'Refund clawback' },
  } as Record<string, Entry>,
} satisfies Record<string, unknown>

export const errors: Record<string, Entry> = {
  network_error: { ko: '서버에 연결하지 못했어요. 네트워크를 확인해 주세요', zh: '無法連線至伺服器，請檢查網路', en: 'Could not reach the server. Check your connection' },
  service_unavailable: { ko: '서비스를 잠시 쓸 수 없어요. 잠시 후 다시 시도해 주세요', zh: '服務暫時無法使用，請稍後再試', en: 'The service is temporarily unavailable. Try again shortly' },
  invalid_request: { ko: '입력값을 다시 확인해 주세요', zh: '請檢查填寫的內容', en: 'Please check the fields you entered' },
  bad_payload: { ko: '요청 형식이 올바르지 않아요', zh: '請求格式不正確', en: 'The request is malformed' },
  invalid_credentials: { ko: '이메일 또는 비밀번호가 맞지 않아요', zh: '電子郵件或密碼不正確', en: 'Email or password is incorrect' },
  account_locked: { ko: '잠긴 계정이에요. 고객센터에 문의해 주세요', zh: '帳號已被鎖定，請聯絡客服', en: 'This account is locked. Contact support' },
  account_inactive: { ko: '사용할 수 없는 계정이에요', zh: '帳號無法使用', en: 'This account is not active' },
  account_unavailable: { ko: '사용할 수 없는 계정이에요', zh: '帳號無法使用', en: 'This account is not available' },
  account_not_found: { ko: '이 이메일로 가입한 계정이 없어요. 먼저 가입해 달라고 알려 주세요', zh: '沒有使用此電子郵件的帳號，請對方先註冊', en: 'No account uses this email. Ask them to sign up first' },
  email_unverified: { ko: '이메일 인증을 먼저 마쳐 주세요', zh: '請先完成電子郵件驗證', en: 'Please verify your email first' },
  email_unavailable: { ko: '이 이메일은 쓸 수 없어요', zh: '此電子郵件無法使用', en: 'This email cannot be used' },
  weak_password: { ko: '비밀번호는 10자 이상, 숫자나 문자만으로 만들 수 없어요', zh: '密碼至少 10 位，不能只有數字或只有字母', en: 'Use at least 10 characters, not only digits or only letters' },
  invalid_code: { ko: '인증 코드가 틀렸거나 만료됐어요', zh: '驗證碼錯誤或已過期', en: 'The code is invalid or expired' },
  invalid_totp: { ko: '인증 코드가 맞지 않아요', zh: '動態碼不正確', en: 'The authentication code is incorrect' },
  invalid_recovery_code: { ko: '복구 코드가 맞지 않거나 이미 사용됐어요', zh: '備用碼不正確或已使用過', en: 'The recovery code is invalid or already used' },
  totp_required: { ko: '2단계 인증 코드가 필요해요', zh: '需要雙因素驗證動態碼', en: 'Two-factor code required' },
  totp_already_enabled: { ko: '2단계 인증이 이미 켜져 있어요', zh: '雙因素認證已經開啟', en: 'Two-factor authentication is already on' },
  totp_not_enabled: { ko: '2단계 인증이 꺼져 있어요', zh: '雙因素認證尚未開啟', en: 'Two-factor authentication is off' },
  totp_not_setup: { ko: '설정을 처음부터 다시 시작해 주세요', zh: '請重新開始設定', en: 'Start the setup again' },
  too_many_attempts: { ko: '시도가 너무 많아요. 잠시 후 다시 해 주세요', zh: '嘗試次數過多，請稍後再試', en: 'Too many attempts. Try again later' },
  not_logged_in: { ko: '로그인이 필요해요', zh: '請先登入', en: 'Please log in' },
  token_expired: { ko: '로그인이 만료됐어요. 다시 로그인해 주세요', zh: '登入已過期，請重新登入', en: 'Your session expired. Please log in again' },
  token_invalid: { ko: '로그인이 만료됐어요. 다시 로그인해 주세요', zh: '登入已失效，請重新登入', en: 'Your session is no longer valid. Please log in again' },
  session_invalid: { ko: '로그인이 만료됐어요. 다시 로그인해 주세요', zh: '登入已過期，請重新登入', en: 'Your session expired. Please log in again' },
  session_revoked: { ko: '다른 곳에서 로그아웃됐어요. 다시 로그인해 주세요', zh: '工作階段已登出，請重新登入', en: 'This session was signed out. Please log in again' },
  refresh_raced: { ko: '잠시 후 다시 시도해 주세요', zh: '請稍後再試', en: 'Please retry' },
  bad_origin: { ko: '허용되지 않은 출처의 요청이에요', zh: '請求來源不被允許', en: 'Cross-origin request rejected' },
  ip_not_allowed: { ko: '이 네트워크에서는 관리자 페이지를 쓸 수 없어요', zh: '目前網路不允許存取管理後台', en: 'Admin access is not allowed from this network' },
  not_operator: { ko: '운영 계정이 아니에요', zh: '此帳號不是營運帳號', en: 'This account is not an operator' },
  insufficient_role: { ko: '이 작업은 관리자 권한이 필요해요', zh: '此操作需要管理員權限', en: 'This action requires the admin role' },
  forbidden: { ko: '권한이 없어요', zh: '沒有權限', en: 'You do not have permission' },
  insufficient_scope: { ko: '권한이 없어요', zh: '沒有權限', en: 'You do not have permission' },
  not_found: { ko: '찾을 수 없어요', zh: '找不到項目', en: 'Not found' },
  cannot_change_self: { ko: '자기 자신은 바꿀 수 없어요', zh: '不能修改自己', en: 'You cannot change yourself' },
  operator_cannot_delete: { ko: '운영 계정은 탈퇴할 수 없어요', zh: '營運帳號不能註銷', en: 'Operator accounts cannot be deleted' },
  consent_expired: { ko: '승인 요청이 만료됐어요. 앱에서 다시 시작해 주세요', zh: '授權請求已過期，請回到應用程式重新發起', en: 'This request expired. Start again from the app' },
  too_many_keys: { ko: 'API Key 개수 한도에 도달했어요', zh: 'API Key 數量已達上限', en: 'API key limit reached' },
  system_key_protected: { ko: '시스템 Key는 폐기할 수 없어요', zh: '系統 Key 不能撤銷', en: 'System keys cannot be revoked' },
  key_revoked: { ko: '이미 폐기된 Key예요', zh: 'Key 已撤銷', en: 'This key is revoked' },
  rpm_too_high: { ko: '분당 요청 수가 허용 범위를 넘었어요', zh: '每分鐘請求數超出允許範圍', en: 'Requests per minute is above the allowed maximum' },
  concurrency_too_high: { ko: '동시 요청 수가 허용 범위를 넘었어요', zh: '並行數超出允許範圍', en: 'Concurrency is above the allowed maximum' },
  priority_admin_only: { ko: '우선순위는 운영자만 바꿀 수 있어요', zh: '優先順序僅限營運人員修改', en: 'Only operators can change priority' },
  expiry_in_past: { ko: '만료 시간은 미래여야 해요', zh: '到期時間必須晚於現在', en: 'Expiry must be in the future' },
  invalid_range: { ko: '기간은 92일을 넘을 수 없어요', zh: '時間範圍不能超過 92 天', en: 'The range must be at most 92 days' },
  invalid_time: { ko: '시간 형식이 올바르지 않아요', zh: '時間格式不正確', en: 'Invalid time' },
  invalid_cursor: { ko: '목록을 처음부터 다시 불러와 주세요', zh: '請從頭重新載入列表', en: 'Reload the list from the start' },
  invalid_group_by: { ko: '지원하지 않는 묶음 기준이에요', zh: '不支援的分組方式', en: 'Unsupported grouping' },
  payment_unavailable: { ko: '지금은 결제를 쓸 수 없어요', zh: '付款功能暫時無法使用', en: 'Payments are not available right now' },
  payment_channel_error: { ko: '결제사 응답에 문제가 있어요. 잠시 후 다시 해 주세요', zh: '付款通道發生錯誤，請稍後再試', en: 'The payment provider returned an error. Try again later' },
  product_not_found: { ko: '상품을 찾을 수 없어요', zh: '找不到商品', en: 'Product not found' },
  not_cancelable: { ko: '이 주문은 취소할 수 없어요', zh: '此訂單無法取消', en: 'This order cannot be canceled' },
  not_refundable: { ko: '이 주문은 환불할 수 없어요', zh: '此訂單無法退款', en: 'This order cannot be refunded' },
  invalid_amount: { ko: '금액이 올바르지 않아요', zh: '金額不正確', en: 'Invalid amount' },
  invalid_transition: { ko: '현재 상태에서는 할 수 없는 작업이에요', zh: '目前狀態下無法執行此操作', en: 'Not allowed in the current state' },
  ref_conflict: { ko: '같은 요청 번호로 다른 내용이 이미 처리됐어요', zh: '同一請求編號已處理過不同內容', en: 'This reference was already used for a different request' },
  kind_immutable: { ko: '모델 종류는 바꿀 수 없어요. 새 모델 ID로 만들어 주세요', zh: '模型類型無法更改，請建立新的模型 ID', en: 'A model kind cannot change; use a new model id' },
  duplicate_route: { ko: '같은 채널과 상위 모델이 중복됐어요', zh: '通道與上游模型重複', en: 'Duplicate channel and upstream model' },
  unknown_channel: { ko: '없는 채널이 있어요', zh: '有不存在的通道', en: 'Unknown channel' },
  api_key_required: { ko: '새 채널은 API Key가 필요해요', zh: '新增通道必須填寫 API Key', en: 'An API key is required for a new channel' },
  invalid_pricing: { ko: '가격 설정이 올바르지 않아요', zh: '價格設定不合法', en: 'Invalid pricing configuration' },
  invalid_thinking_policy: { ko: '생각 모드 정책이 올바르지 않아요', zh: '思考策略不合法', en: 'Invalid thinking policy' },
  invalid_overrides: { ko: '노드 요청 파라미터가 올바르지 않아요', zh: '節點請求參數不合法', en: 'Invalid node request parameters' },
  invalid_upstream_model: { ko: '상위 모델명이 올바르지 않아요', zh: '上游模型名稱不合法', en: 'Invalid upstream model' },
  unregistered_channel_model: { ko: '채널 모델 라이브러리에 없는 모델이 있어요', zh: '有路由引用了通道模型庫裡沒有的模型', en: 'A route uses a model missing from the channel model library' },
  in_use: { ko: '다른 곳에서 사용 중이라 지울 수 없어요', zh: '正在使用中，無法刪除', en: 'In use; cannot delete' },
  pricing_admin_only: { ko: '가격 배율은 관리자만 바꿀 수 있어요', zh: '價格倍率僅限管理員修改', en: 'Only admins can change pricing' },
  invalid_avatar: { ko: 'JPG, PNG, WEBP, GIF 형식의 이미지를 선택해 주세요', zh: '請選擇 JPG / PNG / WEBP / GIF 格式的圖片', en: 'Please choose a JPG, PNG, WEBP or GIF image' },
  avatar_too_large: { ko: '이미지는 5MB 이하여야 해요', zh: '圖片不能超過 5 MB', en: 'Image must be 5 MB or smaller' },
  invalid_birthday_format: { ko: '생년월일을 모두 선택해 주세요', zh: '請完整選擇出生年、月、日', en: 'Please select year, month, and day' },
  birthday_out_of_range: { ko: '생년월일은 1900-01-01부터 오늘 사이여야 해요', zh: '出生日期需在 1900-01-01 至今天之間', en: 'Date of birth must be between 1900-01-01 and today' },
  identity_unavailable: { ko: '계정 서비스에 잠시 연결할 수 없어요', zh: '帳號服務暫時無法使用', en: 'The account service is temporarily unavailable' },
  internal_error: { ko: '서버 오류가 났어요. 잠시 후 다시 해 주세요', zh: '伺服器發生錯誤，請稍後再試', en: 'Server error. Try again later' },
}

const DETAILED = new Set(['invalid_pricing', 'invalid_thinking_policy', 'invalid_overrides', 'invalid_upstream_model', 'unregistered_channel_model'])

const fallback: Entry = { ko: '요청을 처리하지 못했어요 ({code})', zh: '操作失敗（{code}）', en: 'Request failed ({code})' }

/** 把接口错误转成当前语言的提示。 */
export function errorText(err: unknown, locale: Locale): string {
  const code =
    err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : 'internal_error'
  const entry = errors[code]
  // 配置校验类错误的原因只有服务端知道（哪一项价目不合法），附在文案后面方便运营定位
  const detail = DETAILED.has(code) && err instanceof Error && err.message ? `: ${err.message}` : ''
  if (entry) return translate(entry, locale) + detail
  return translate(fallback, locale, { code })
}
